import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import AssistantWidget from "./AssistantWidget";
import SessionGuard from "./SessionGuard";
import {
  SquaresFour,
  PlugsConnected,
  Database,
  ArrowsLeftRight,
  Megaphone,
  Gear,
  Table,
  Plugs,
  PencilSimpleLine,
  ChartLineUp,
  Brain,
  AddressBook,
  UsersThree,
  HardHat,
  Clock,
  Cube,
  ShieldCheck,
  House,
  Lock,
  SignOut,
  Briefcase,
  ChartBar,
  Notepad,
} from "@phosphor-icons/react";

const GBE_LOGO =
  "https://customer-assets.emergentagent.com/job_aroflo-hub/artifacts/0o4hadpf_gbe-logo-with-yellow-strip.jpg";

const ADMIN_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: SquaresFour, testid: "nav-dashboard" },
  { to: "/reports", label: "Reports", icon: ChartBar, testid: "nav-reports" },
  { to: "/clients", label: "Clients", icon: AddressBook, testid: "nav-clients" },
  { to: "/client-groups", label: "Client Groups", icon: UsersThree, testid: "nav-client-groups" },
  { to: "/jobs", label: "Jobs", icon: Briefcase, testid: "nav-jobs" },
  { to: "/technicians", label: "Technicians", icon: HardHat, testid: "nav-technicians" },
  { to: "/timesheets", label: "Timesheets", icon: Clock, testid: "nav-timesheets" },
  { to: "/connection", label: "API Connections Status", icon: PlugsConnected, testid: "nav-connection" },
  { to: "/aroflo-options", label: "Aroflo API (v2)", icon: Plugs, testid: "nav-aroflo-options" },
  { to: "/legacy-api", label: "Aroflo API (v1)", icon: Database, testid: "nav-legacy-api" },
  { to: "/maximo", label: "MAXIMO API", icon: ArrowsLeftRight, testid: "nav-maximo" },
  { to: "/mex", label: "MEX API", icon: ArrowsLeftRight, testid: "nav-mex" },
  { to: "/viewer", label: "Data Viewer", icon: Table, testid: "nav-viewer" },
  { to: "/intelligence", label: "Asset Intelligence", icon: Brain, testid: "nav-intelligence" },
  { to: "/intelligence-sample", label: "Asset Intelligence - Sample", icon: ChartLineUp, testid: "nav-intelligence-sample" },
  { to: "/asset-intelligence-v2", label: "Asset Intelligence - Sample V2", icon: Brain, testid: "nav-intelligence-v2" },
  { to: "/scheduler", label: "Scheduler", icon: Clock, testid: "nav-scheduler" },
  { to: "/asset-list", label: "Asset List", icon: Cube, testid: "nav-asset-list" },
  { to: "/asset-updater", label: "Bulk Asset Updater", icon: PencilSimpleLine, testid: "nav-asset-updater" },
  { to: "/admin", label: "User Management", icon: ShieldCheck, testid: "nav-admin" },
  { to: "/logs", label: "Activity Logs", icon: Notepad, testid: "nav-logs" },
  { to: "/cover", label: "Cover Pages", icon: Megaphone, testid: "nav-cover" },
  { to: "/site-settings", label: "Site Settings", icon: Gear, testid: "nav-site-settings" },
  { to: "/security", label: "Security", icon: Lock, testid: "nav-security" },
];

const CLIENT_NAV = [
  { to: "/home", label: "My Dashboard", icon: House, testid: "nav-home" },
  { to: "/reports", label: "Reports", icon: ChartBar, testid: "nav-reports" },
  { to: "/asset-intelligence-v2", label: "Asset Intelligence - V2", icon: Brain, testid: "nav-intelligence-v2" },
  { to: "/security", label: "Security", icon: ShieldCheck, testid: "nav-security" },
];

export default function Layout({ children, wide = false }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const NAV = user?.role === "admin" ? ADMIN_NAV : CLIENT_NAV;

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const initials = (user?.name || user?.email || "?")
    .split(" ")
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <SessionGuard />
      {user?.role === "admin" && <AssistantWidget />}
      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r border-neutral-200 bg-white lg:flex">
        <div className="flex h-16 items-center gap-3 border-b border-neutral-200 px-5">
          <img src={GBE_LOGO} alt="GBE Group" className="h-9 w-9 rounded-sm object-cover" data-testid="brand-logo" />
          <div>
            <p className="font-heading text-base font-black leading-none tracking-tighter text-[#0A0A0A]">
              ASSET HUB
            </p>
            <p className="text-[10px] uppercase tracking-widest text-neutral-500">GBE Group</p>
          </div>
        </div>
        <div className="border-b border-neutral-200 p-3">
          <div className="mb-2 flex items-center gap-3 rounded-sm px-2 py-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-sm bg-[#0A0A0A] text-xs font-bold text-white">
              {initials}
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-[#0A0A0A]" data-testid="current-user-name">
                {user?.name || "User"}
              </p>
              <p className="truncate text-xs text-neutral-500">{user?.email}</p>
            </div>
          </div>
          <button
            data-testid="logout-button"
            onClick={handleLogout}
            className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-sm font-medium text-neutral-700 transition-all duration-200 hover:bg-neutral-100"
          >
            <SignOut size={18} weight="bold" />
            Sign out
          </button>
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto p-3">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-sm px-3 py-2.5 text-sm font-medium transition-all duration-200 ${
                  isActive
                    ? "bg-[#0055FF] text-white"
                    : "text-neutral-700 hover:bg-neutral-100"
                }`
              }
            >
              <item.icon size={18} weight="bold" />
              {item.label}
            </NavLink>
          ))}
        </nav>
      </aside>

      {/* Mobile top bar */}
      <header className="sticky top-0 z-20 flex h-14 items-center justify-between border-b border-neutral-200 bg-white/90 px-4 backdrop-blur-md lg:hidden">
        <div className="flex items-center gap-2">
          <img src={GBE_LOGO} alt="GBE Group" className="h-7 w-7 rounded-sm object-cover" />
          <span className="font-heading font-black tracking-tighter">ASSET HUB</span>
        </div>
        <button data-testid="logout-button-mobile" onClick={handleLogout} className="text-neutral-700">
          <SignOut size={20} weight="bold" />
        </button>
      </header>

      {/* Mobile nav */}
      <nav className="sticky top-14 z-10 flex gap-1 overflow-x-auto border-b border-neutral-200 bg-white px-3 py-2 lg:hidden">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              `whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ${
                isActive ? "bg-[#0055FF] text-white" : "text-neutral-700"
              }`
            }
          >
            {item.label}
          </NavLink>
        ))}
      </nav>

      <main className="lg:pl-64">
        <div className={`mx-auto p-6 sm:p-8 ${wide ? "max-w-none" : "max-w-7xl"}`}>{children}</div>
      </main>
    </div>
  );
}
